import React, {createContext, useContext, useReducer} from "react";
import {useTranslation} from "react-i18next";
import {AuthsProvider} from "../auths/authsContext";
import {CssBaseline} from "@mui/material";
import {AppHeader} from "../header/AppHeader";
import {MuseumFilterProvider} from '@ekultur/museum-filter'
import {WithApplications} from "../application/WithApplications";
import {WithMuseums} from "../museum/WithMuseums";
import {mapFromRepository} from "../museum/museumMapper";

export const MUSEUMS_RECEIVED = 'museumsReceived';
export const APPS_RECEIVED = 'appsReceived';
export const RENDERED = 'rendered';
export const MENU_OPENED = 'menuOpened';
export const MENU_CLOSED = 'menuClosed';
export const REFRESH_MUSEUMS_TRIGGERED = 'refreshMuseumsTriggered'
export const REFRESH_APPLICATIONS_TRIGGERED = 'refreshApplicationsTriggered'

const AppStateContext = createContext(undefined);
const AppDispatchContext = createContext(undefined);
const AppTranslationContext = createContext(undefined);

const initialState = {
    museums: [],
    apps: [],
    menuOpen: true,
    rendered: false,
    refreshMuseums: 0,
    refreshApplications: 0
}

const appReducer = (state, action) => {
    console.debug(action);
    switch (action.type) {
        case REFRESH_MUSEUMS_TRIGGERED:
            return {
                ...state,
                refreshMuseums: state.refreshMuseums + 1
        }
        case REFRESH_APPLICATIONS_TRIGGERED:
            return {
                ...state,
                refreshApplications: state.refreshApplications + 1
            }
        case MUSEUMS_RECEIVED:
            return {
                ...state,
                museums: action.museums
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(mapFromRepository)
            }
        case APPS_RECEIVED:
            return {
                ...state,
                apps: action.apps.map(app => ({
                    ...app,
                    adminFrontend: app.admin_frontend,
                    hasFrontend: app.has_frontend,
                    iconUrl: app.icon_url,
                    loginLogo: app.login_logo,
                    theme: {
                        ...app.theme,
                        lightColor: app.theme.light_color,
                        darkColor: app.theme.dark_color,
                        primaryColor: app.theme.primary_color,
                    },
                    translationId: app.translation_id,
                    displayTitle: app.display_title
                }))
            }
        case RENDERED:
            return {
                ...state,
                rendered: true
            }
        case MENU_OPENED:
            return {
                ...state,
                menuOpen: true
            }
        case MENU_CLOSED:
            return {
                ...state,
                menuOpen: false
            }
        default:
            throw new Error(`Unhandled action type ${action.type}`)
    }
}

export const AppProvider = ({children}) => {
    const [state, dispatch] = useReducer(appReducer, {...initialState}, undefined);
    const {t} = useTranslation('inbox.app', {useSuspense: false});

    return (
        <AppStateContext.Provider value={state}>
            <AppDispatchContext.Provider value={dispatch}>
                <AppTranslationContext.Provider value={t}>
                    <WithMuseums>
                        <WithApplications>
                            <MuseumFilterProvider>
                                <AuthsProvider>
                                    <CssBaseline/>
                                    <AppHeader/>
                                    {children}
                                </AuthsProvider>
                            </MuseumFilterProvider>
                        </WithApplications>
                    </WithMuseums>
                </AppTranslationContext.Provider>
            </AppDispatchContext.Provider>
        </AppStateContext.Provider>
    )
}

export const useAppState = () => {
    const context = useContext(AppStateContext);
    if (undefined === context) {
        throw new Error(`useAppState must be used witin an AppProvider`);
    } else {
        return context;
    }
}

export const useAppDispatch = () => {
    const context = useContext(AppDispatchContext);
    if (undefined === context) {
        throw new Error(`useAppDispatch must be used within an AppProvider`);
    } else {
        return context;
    }
}

export const useAppTranslation = () => {
    const context = useContext(AppTranslationContext);
    if (undefined === context) {
        throw new Error("useAppTranslation must be used within an AppProvider")
    } else {
        return context;
    }
}